import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ComboModel } from 'app/shared/components/combo/combo-model';
import { NoteModel } from 'app/shared/components/note/note.model';
import { ActionConfirmationComboDialogComponent } from './action-confirmation-combo-dialog/action-confirmation-combo-dialog.component';
import { ComboDialogBoxDataModel } from './action-confirmation-combo-dialog/combo-dialog-box-data.model';
import { ActionConfirmationDialogComponent } from './action-confirmation-dialog/action-confirmation-dialog.component';
import { DialogBoxDataModel } from './action-confirmation-dialog/dialog-box-data.model';
import { DialogBoxResultModel } from './action-confirmation-dialog/dialog-box-result.model';
import { ActionConfirmationInformationDialogComponent } from './action-confirmation-information-dialog/action-confirmation-information-dialog.component';
import { InformationDialogBoxDataModel } from './action-confirmation-information-dialog/information-dialog-box-data.model';
import { ActionConfirmationMessageDialogComponent } from './action-confirmation-message-dialog/action-confirmation-message-dialog.component';
import { MessageDialogBoxDataModel } from './action-confirmation-message-dialog/message-dialog-box-data.model';
import { ActionConfirmationNumberDialogComponent } from './action-confirmation-number-dialog/action-confirmation-number-dialog.component';
import { NumberDialogBoxDataModel } from './action-confirmation-number-dialog/number-dialog-box-data.model';

@Injectable({
  providedIn: 'root',
})
export class DialogBoxService {
  private _dialog = inject(MatDialog);

  public promptForAction({
    acceptLabel = 'Oui',
    cdkFocusInitialOnCancelButtonFlag = true,
    declineLabel = 'Non',
    messageLine1: messageLine1,
    messageLine2: messageLine2 = null,
    note = null,
    onAccept: onAccept = null,
    onDecline: onDecline = () => {},
    subMessageLine1: subMessageLine1 = null,
    subMessageLine2: subMessageLine2 = null,
  }: {
    acceptLabel?: string;
    cdkFocusInitialOnCancelButtonFlag?: boolean;
    declineLabel?: string;
    messageLine1: string;
    messageLine2?: string | null;
    note?: NoteModel | null;
    onAccept: (() => void) | null;
    onDecline?: () => void;
    subMessageLine1?: string | null;
    subMessageLine2?: string | null;
  }): MatDialogRef<ActionConfirmationDialogComponent, unknown> {
    const data: DialogBoxDataModel = {
      acceptLabel: acceptLabel,
      cdkFocusInitialOnButtonFlag: true,
      cdkFocusInitialOnCancelButtonFlag: cdkFocusInitialOnCancelButtonFlag,
      declineLabel: declineLabel,
      messageLine1: messageLine1,
      messageLine2: messageLine2,
      note: note,
      onAccept: onAccept,
      onAcceptWithOption: undefined,
      onDecline: onDecline,
      optionFlag: false,
      optionLabel: undefined,
      optionSubLabel: undefined,
      optionValueFlag: undefined,
      requireValidationFlag: false,
      subMessageLine1: subMessageLine1,
      subMessageLine2: subMessageLine2,
    };
    const dialogRef = this._dialog.open(ActionConfirmationDialogComponent, {
      data: data,
      autoFocus: false,
    });

    return dialogRef;
  }

  public promptForActionWithCombo({
    acceptLabel = 'Valider',
    cdkFocusInitialOnCancelButtonFlag = true,
    comboPlaceHolder = null,
    declineLabel = 'Annuler',
    messageLine1: messageLine1,
    messageLine2: messageLine2 = null,
    note = null,
    onAcceptWithCombo,
    onDecline = null,
    options: options,
    selectedValue = null,
    subMessageLine1: subMessageLine1 = null,
    subMessageLine2: subMessageLine2 = null,
  }: {
    acceptLabel?: string;
    cdkFocusInitialOnCancelButtonFlag?: boolean;
    comboPlaceHolder?: string | null;
    declineLabel?: string;
    messageLine1: string;
    messageLine2?: string | null;
    note?: NoteModel | null;
    onAcceptWithCombo: (item: ComboModel) => void;
    onDecline: (() => void) | null;
    options: ComboModel[];
    selectedValue?: ComboModel | null;
    subMessageLine1?: string | null;
    subMessageLine2?: string | null;
  }): MatDialogRef<ActionConfirmationComboDialogComponent, unknown> {
    const data: ComboDialogBoxDataModel = {
      acceptLabel: acceptLabel,
      cdkFocusInitialOnButtonFlag: true,
      cdkFocusInitialOnCancelButtonFlag: cdkFocusInitialOnCancelButtonFlag,
      comboPlaceHolder: comboPlaceHolder,
      declineLabel: declineLabel,
      messageLine1: messageLine1,
      messageLine2: messageLine2,
      note: note,
      onAcceptWithCombo: onAcceptWithCombo,
      onDecline: onDecline,
      options: options,
      selectedValue: selectedValue,
      subMessageLine1: subMessageLine1,
      subMessageLine2: subMessageLine2,
    };
    const dialogRef = this._dialog.open(ActionConfirmationComboDialogComponent, {
      data: data,
      autoFocus: false,
    });

    return dialogRef;
  }

  public promptForActionWithInformation({
    acceptLabel = 'Oui',
    cdkFocusInitialOnCancelButtonFlag = true,
    declineLabel = 'Non',
    htmlMessage = '',
    messageLine1: messageLine1,
    messageLine2: messageLine2 = null,
    note = null,
    onAccept: onAccept = null,
    onDecline: onDecline = () => {},
    subMessageLine1: subMessageLine1 = null,
    subMessageLine2: subMessageLine2 = null,
  }: {
    acceptLabel?: string;
    cdkFocusInitialOnCancelButtonFlag?: boolean;
    declineLabel?: string;
    htmlMessage: string;
    messageLine1: string;
    messageLine2?: string | null;
    note?: NoteModel | null;
    onAccept: (() => void) | null;
    onDecline?: () => void;
    subMessageLine1?: string | null;
    subMessageLine2?: string | null;
  }): MatDialogRef<ActionConfirmationInformationDialogComponent, unknown> {
    const data: InformationDialogBoxDataModel = {
      acceptLabel: acceptLabel,
      cdkFocusInitialOnButtonFlag: true,
      cdkFocusInitialOnCancelButtonFlag: cdkFocusInitialOnCancelButtonFlag,
      declineLabel: declineLabel,
      messageLine1: messageLine1,
      messageLine2: messageLine2,
      note: note,
      onAccept: onAccept,
      onDecline: onDecline,
      subMessageLine1: subMessageLine1,
      subMessageLine2: subMessageLine2,
      htmlMessage: htmlMessage,
    };
    const dialogRef = this._dialog.open(ActionConfirmationInformationDialogComponent, {
      data: data,
      autoFocus: false,
    });

    return dialogRef;
  }

  public promptForActionWithMessage({
    acceptLabel = 'Oui',
    cdkFocusInitialOnCancelButtonFlag = true,
    declineLabel = 'Non',
    message = null,
    messageLine1: messageLine1,
    messageLine2: messageLine2 = null,
    note: note = null,
    onAccept,
    onDecline: onDecline = null,
    subMessageLine1 = null,
    subMessageLine2 = null,
  }: {
    acceptLabel?: string;
    cdkFocusInitialOnCancelButtonFlag?: boolean;
    declineLabel?: string;
    messageLine1: string;
    messageLine2?: string | null;
    message?: string | null;
    note?: NoteModel | null;
    onAccept: (message: string) => void;
    onDecline: (() => void) | null;
    subMessageLine1?: string | null;
    subMessageLine2?: string | null;
  }): MatDialogRef<ActionConfirmationMessageDialogComponent, unknown> {
    const data: MessageDialogBoxDataModel = {
      acceptLabel: acceptLabel,
      cdkFocusInitialOnButtonFlag: true,
      cdkFocusInitialOnCancelButtonFlag: cdkFocusInitialOnCancelButtonFlag,
      declineLabel: declineLabel,
      messageLine1: messageLine1,
      messageLine2: messageLine2,
      message: message,
      note: note,
      onAccept: onAccept,
      onDecline: onDecline,
      subMessageLine1: subMessageLine1,
      subMessageLine2: subMessageLine2,
    };
    const dialogRef = this._dialog.open(ActionConfirmationMessageDialogComponent, {
      data: data,
      autoFocus: false,
    });

    return dialogRef;
  }

  public promptForActionWithNumber({
    acceptLabel = 'Oui',
    declineLabel = 'Non',
    messageLine1: messageLine1,
    messageLine2: messageLine2 = null,
    note: note = null,
    numberPatternErrorLabel = null,
    numberMatLabel = null,
    numberRequiredErrorLabel = null,
    numberValue = 0,
    onAccept,
    onDecline: onDecline = null,
    subMessageLine1 = null,
    subMessageLine2 = null,
  }: {
    acceptLabel?: string;
    declineLabel?: string;
    messageLine1: string;
    messageLine2?: string | null;
    note?: NoteModel | null;
    numberPatternErrorLabel?: string | null;
    numberMatLabel?: string | null;
    numberRequiredErrorLabel?: string | null;
    numberValue?: number;
    onAccept: (number: number) => void;
    onDecline: (() => void) | null;
    subMessageLine1?: string | null;
    subMessageLine2?: string | null;
  }): MatDialogRef<ActionConfirmationNumberDialogComponent, unknown> {
    const data: NumberDialogBoxDataModel = {
      acceptLabel: acceptLabel,
      cdkFocusInitialOnButtonFlag: false,
      cdkFocusInitialOnCancelButtonFlag: true,
      declineLabel: declineLabel,
      messageLine1: messageLine1,
      messageLine2: messageLine2,
      note: note,
      numberPatternErrorLabel: numberPatternErrorLabel,
      numberMatLabel: numberMatLabel,
      numberRequiredErrorLabel: numberRequiredErrorLabel,
      numberValue: numberValue,
      onAccept: onAccept,
      onDecline: onDecline,
      subMessageLine1: subMessageLine1,
      subMessageLine2: subMessageLine2,
    };
    const dialogRef = this._dialog.open(ActionConfirmationNumberDialogComponent, {
      data: data,
      autoFocus: false,
    });

    return dialogRef;
  }

  public promptForActionWithOption({
    acceptLabel = 'Oui',
    cdkFocusInitialOnCancelButtonFlag = true,
    declineLabel = 'Non',
    messageLine1: messageLine1,
    messageLine2: messageLine2 = null,
    note = null,
    onAcceptWithOption,
    onDecline = null,
    optionFlag = true,
    optionLabel,
    optionSubLabel = null,
    optionValueFlag = false,
    requireValidationFlag = false,
    subMessageLine1: subMessageLine1 = null,
    subMessageLine2: subMessageLine2 = null,
  }: {
    acceptLabel?: string;
    cdkFocusInitialOnCancelButtonFlag?: boolean;
    declineLabel?: string;
    messageLine1: string;
    messageLine2?: string | null;
    note?: NoteModel | null;
    onAcceptWithOption: (result: DialogBoxResultModel) => void;
    onDecline: (() => void) | null;
    optionFlag?: boolean;
    optionLabel: string;
    optionSubLabel?: string | null;
    optionValueFlag?: boolean;
    requireValidationFlag?: boolean;
    subMessageLine1?: string | null;
    subMessageLine2?: string | null;
  }): MatDialogRef<ActionConfirmationDialogComponent, unknown> {
    const data: DialogBoxDataModel = {
      acceptLabel: acceptLabel,
      cdkFocusInitialOnButtonFlag: true,
      cdkFocusInitialOnCancelButtonFlag: cdkFocusInitialOnCancelButtonFlag,
      declineLabel: declineLabel,
      messageLine1: messageLine1,
      messageLine2: messageLine2,
      note: note,
      onAccept: undefined,
      onAcceptWithOption: onAcceptWithOption,
      onDecline: onDecline,
      optionFlag: optionFlag,
      optionLabel: optionLabel,
      optionSubLabel: optionSubLabel,
      optionValueFlag: optionValueFlag,
      requireValidationFlag: requireValidationFlag,
      subMessageLine1: subMessageLine1,
      subMessageLine2: subMessageLine2,
    };
    const dialogRef = this._dialog.open(ActionConfirmationDialogComponent, {
      data: data,
      autoFocus: false,
    });

    return dialogRef;
  }
}
